import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { BinaryAtlas } from './binary/BinaryAtlas.js';
import { getCityOffset, getCitySpan } from '../world/city/cityLayout.js';
import { Random } from '../utils/Random.js';
import { debugLog } from '../debug/debugLog.js';

const vertexShader = /* glsl */ `
attribute float aGlyphIndex;
attribute vec2 aStreamAnchor;
attribute vec4 aStreamParams;

uniform float uTime;
uniform float uWindTime;
uniform vec2 uWindStrength;
uniform vec3 uCameraPosition;
uniform float uRainTop;
uniform float uFallRange;
uniform float uGlyphSpacing;
uniform float uLodNearDist;
uniform float uLodFarDist;
uniform float uLodNearGlyphs;
uniform float uLodFarGlyphs;
uniform float uLodNearScale;
uniform float uLodFarScale;

varying vec2 vUv;
varying float vBrightness;
varying float vGlyphIndex;
varying float vStreamSeed;

void main() {
  vUv = uv;
  float speed = aStreamParams.x;
  float streamLength = aStreamParams.y;
  float streamBrightness = aStreamParams.z;
  float seed = aStreamParams.w;
  vStreamSeed = seed;
  vGlyphIndex = aGlyphIndex;

  if (aGlyphIndex >= streamLength) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  vec2 camXZ = uCameraPosition.xz;
  float dist = distance(camXZ, aStreamAnchor);
  float lodMix = 1.0 - smoothstep(uLodNearDist, uLodFarDist, dist);
  float effectiveLength = mix(uLodFarGlyphs, uLodNearGlyphs, lodMix);
  effectiveLength = min(streamLength, effectiveLength);

  if (aGlyphIndex >= effectiveLength) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  float glyphScale = mix(uLodFarScale, uLodNearScale, lodMix);

  float windX =
    sin(uWindTime * 0.73 + seed * 4.17) * uWindStrength.x +
    cos(uWindTime * 0.41 + seed * 2.31) * uWindStrength.y * 0.65;
  float windZ =
    cos(uWindTime * 0.55 + seed * 1.83) * uWindStrength.x * 0.35;

  float phase = uTime * speed + seed * 17.0;
  float headY = uRainTop - mod(phase, uFallRange);
  float worldY = headY - aGlyphIndex * uGlyphSpacing;

  vec3 worldCenter = vec3(
    aStreamAnchor.x + windX,
    worldY,
    aStreamAnchor.y + windZ
  );

  float tailT = aGlyphIndex / max(effectiveLength - 1.0, 1.0);
  float headBoost = 1.0 - step(0.5, aGlyphIndex);
  float tailFade = mix(1.0, 0.15, tailT);
  vBrightness = streamBrightness * tailFade * (0.72 + 0.28 * headBoost);

  vec3 cameraRight = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  vec3 cameraUp = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);

  vec3 billboard =
    worldCenter +
    cameraRight * position.x * glyphScale +
    cameraUp * position.y * glyphScale;

  vec4 mvPosition = viewMatrix * vec4(billboard, 1.0);
  gl_Position = projectionMatrix * mvPosition;
}
`;

const fragmentShader = /* glsl */ `
uniform float uTime;
uniform sampler2D uAtlas;
uniform vec3 uColorBase;
uniform vec3 uColorGlow;
uniform float uFlipSpeed;
uniform float uGlowStrength;

varying vec2 vUv;
varying float vBrightness;
varying float vGlyphIndex;
varying float vStreamSeed;

void main() {
  float tick = floor(
    uTime * uFlipSpeed + vStreamSeed * 13.7 + vGlyphIndex * 2.11
  );
  float digit = mod(tick, 2.0);

  vec2 atlasUv = vec2(vUv.x * 0.5 + digit * 0.5, vUv.y);
  vec4 glyph = texture2D(uAtlas, atlasUv);
  float glyphMask = max(glyph.r, max(glyph.g, glyph.b));

  if (glyphMask < 0.04) {
    discard;
  }

  vec3 emissive = uColorGlow * glyphMask * vBrightness * uGlowStrength;
  vec3 base = uColorBase * (1.0 - glyphMask * 0.55);
  gl_FragColor = vec4(base + emissive, min(1.0, glyphMask * 0.98));
}
`;

/**
 * GPU instanced Matrix digital rain — one InstancedMesh, thousands of glyphs.
 */
export class DigitalRain {
  /**
   * @param {{
   *   config?: import('../config.js').CONFIG['rain'];
   *   graphicsSettings?: import('./GraphicsSettings.js').GraphicsSettings;
   * }} [options]
   */
  constructor({ config = CONFIG.rain, graphicsSettings } = {}) {
    const density =
      graphicsSettings?.rainDensity ?? CONFIG.graphics.rainDensity ?? 1;
    const streamCount = Math.max(
      0,
      Math.floor(config.streamCount * density),
    );
    this.config = {
      ...config,
      streamCount,
      enabled: config.enabled && streamCount > 0 && density > 0,
    };
    this.group = new THREE.Group();
    this.group.name = 'DigitalRain';
    this.group.renderOrder = 5;

    this._time = 0;
    this._windTime = 0;
    this._recycleTimer = 0;
    this._intensityMultiplier = 1;
    this._rng = new Random(this.config.seed);
    this._atlas = null;
    this._geometry = null;
    this._material = null;
    this._mesh = null;

    if (!this.config.enabled || this.config.streamCount === 0) {
      this._streamCount = 0;
      this._maxLength = this.config.maxStreamLength;
      this._instanceCount = 0;
      this._anchors = new Float32Array(0);
      this._glyphIndex = new Float32Array(0);
      this._streamParams = new Float32Array(0);
      this._streamAnchors = new Float32Array(0);
      return;
    }

    this._atlas = new BinaryAtlas({ pixelSize: 64 });

    this._streamCount = this.config.streamCount;
    this._maxLength = this.config.maxStreamLength;
    this._instanceCount = this._streamCount * this._maxLength;

    this._anchors = new Float32Array(this._instanceCount * 2);
    this._glyphIndex = new Float32Array(this._instanceCount);
    this._streamParams = new Float32Array(this._instanceCount * 4);

    /** @type {Float32Array} per-stream anchor for recycling */
    this._streamAnchors = new Float32Array(this._streamCount * 2);

    this._coverage = this._computeCoverage();
    this._initStreams();
    this._buildMesh();
  }

  get instanceCount() {
    return this._instanceCount;
  }

  get streamCount() {
    return this._streamCount;
  }

  /** Rain is visible and simulating (for audio). */
  get isAudible() {
    return (
      this.group.visible &&
      this.config.enabled &&
      this._streamCount > 0
    );
  }

  get intensityMultiplier() {
    return this._intensityMultiplier;
  }

  /**
   * @param {boolean} visible
   */
  setVisible(visible) {
    this.group.visible = visible;
    this.config.enabled = visible && this._streamCount > 0;
  }

  /** @param {number} multiplier */
  setIntensityMultiplier(multiplier) {
    this._intensityMultiplier = Math.max(0.25, multiplier);
    if (this._material) {
      this._material.uniforms.uFlipSpeed.value =
        this.config.flipSpeed * this._intensityMultiplier;
    }
  }

  /**
   * @param {number} deltaSeconds
   * @param {THREE.Vector3} cameraPosition
   */
  update(deltaSeconds, cameraPosition) {
    if (!this.config.enabled || !this._material) return;

    this._time += deltaSeconds * this._intensityMultiplier;
    this._windTime += deltaSeconds * this.config.windSpeed * this._intensityMultiplier;
    const u = this._material.uniforms;
    u.uTime.value = this._time;
    u.uWindTime.value = this._windTime;
    u.uCameraPosition.value.copy(cameraPosition);

    this._recycleTimer += deltaSeconds;
    if (this._recycleTimer >= this.config.recycleInterval) {
      this._recycleTimer = 0;
      this._recycleDistantStreams(cameraPosition);
    }
  }

  dispose() {
    this._geometry?.dispose();
    this._material?.dispose();
    this._atlas?.dispose();
    this.group.clear();
  }

  _computeCoverage() {
    const city = CONFIG.city;
    const span = getCitySpan(city);
    const offset = getCityOffset(city);
    const margin = this.config.spreadMargin;
    return {
      minX: offset - margin,
      maxX: offset + span + margin,
      minZ: offset - margin,
      maxZ: offset + span + margin,
      centerX: offset + span / 2,
      centerZ: offset + span / 2,
    };
  }

  _initStreams() {
    const { speedMin, speedMax, lengthMin, lengthMax, brightnessMin, brightnessMax } =
      this.config;

    for (let s = 0; s < this._streamCount; s += 1) {
      const ax = this._rng.range(this._coverage.minX, this._coverage.maxX);
      const az = this._rng.range(this._coverage.minZ, this._coverage.maxZ);
      this._streamAnchors[s * 2] = ax;
      this._streamAnchors[s * 2 + 1] = az;

      const speed = this._rng.range(speedMin, speedMax);
      const length = this._rng.int(lengthMin, lengthMax);
      const brightness = this._rng.range(brightnessMin, brightnessMax);
      const seed = this._rng.next() * 1000;

      for (let g = 0; g < this._maxLength; g += 1) {
        const idx = s * this._maxLength + g;
        this._anchors[idx * 2] = ax;
        this._anchors[idx * 2 + 1] = az;
        this._glyphIndex[idx] = g;
        const base = idx * 4;
        this._streamParams[base] = speed;
        this._streamParams[base + 1] = length;
        this._streamParams[base + 2] = brightness;
        this._streamParams[base + 3] = seed;
      }
    }
  }

  _buildMesh() {
    const matrix = CONFIG.matrix;
    this._geometry = new THREE.PlaneGeometry(
      this.config.glyphWidth,
      this.config.glyphWidth * 1.35,
    );
    this._geometry.setAttribute(
      'aGlyphIndex',
      new THREE.InstancedBufferAttribute(this._glyphIndex, 1),
    );
    this._geometry.setAttribute(
      'aStreamAnchor',
      new THREE.InstancedBufferAttribute(this._anchors, 2),
    );
    this._geometry.setAttribute(
      'aStreamParams',
      new THREE.InstancedBufferAttribute(this._streamParams, 4),
    );

    const rainGlow = new THREE.Color(matrix.primaryRed);
    rainGlow.multiplyScalar(1.18);
    this._material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uWindTime: { value: 0 },
        uWindStrength: {
          value: new THREE.Vector2(
            this.config.windStrength,
            this.config.windStrength * 0.85,
          ),
        },
        uCameraPosition: { value: new THREE.Vector3() },
        uAtlas: { value: this._atlas.texture },
        uColorBase: { value: new THREE.Color(matrix.darkRed) },
        uColorGlow: { value: rainGlow },
        uFlipSpeed: { value: this.config.flipSpeed },
        uGlowStrength: { value: this.config.glowStrength },
        uRainTop: { value: this.config.rainTop },
        uFallRange: { value: this.config.fallRange },
        uGlyphSpacing: { value: this.config.glyphSpacing },
        uLodNearDist: { value: this.config.lodNearDistance },
        uLodFarDist: { value: this.config.lodFarDistance },
        uLodNearGlyphs: { value: this.config.lodNearGlyphs },
        uLodFarGlyphs: { value: this.config.lodFarGlyphs },
        uLodNearScale: { value: this.config.lodNearGlyphScale },
        uLodFarScale: { value: this.config.lodFarGlyphScale },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
    });

    this._mesh = new THREE.InstancedMesh(
      this._geometry,
      this._material,
      this._instanceCount,
    );
    this._mesh.frustumCulled = false;
    this._mesh.name = 'DigitalRainInstanced';
    this._mesh.count = this._instanceCount;

    const identity = new THREE.Matrix4();
    for (let i = 0; i < this._instanceCount; i += 1) {
      this._mesh.setMatrixAt(i, identity);
    }
    this._mesh.instanceMatrix.needsUpdate = true;

    this.group.add(this._mesh);

    debugLog('[DigitalRain]', {
      streams: this._streamCount,
      glyphs: this._instanceCount,
      coverage: this._coverage,
    });
  }

  /**
   * Reassign stream anchors that drift too far from the camera (wrap, no new meshes).
   * @param {THREE.Vector3} cameraPosition
   */
  _recycleDistantStreams(cameraPosition) {
    const half = this.config.recycleHalfExtent;
    const cx = cameraPosition.x;
    const cz = cameraPosition.z;
    const limit = this.config.recycleRadius;
    let moved = 0;

    const anchorAttr = this._geometry.getAttribute('aStreamAnchor');

    for (let s = 0; s < this._streamCount; s += 1) {
      const sx = this._streamAnchors[s * 2];
      const sz = this._streamAnchors[s * 2 + 1];
      const dx = sx - cx;
      const dz = sz - cz;
      if (Math.hypot(dx, dz) < limit) continue;

      const nx = cx + this._rng.range(-half, half);
      const nz = cz + this._rng.range(-half, half);
      this._streamAnchors[s * 2] = nx;
      this._streamAnchors[s * 2 + 1] = nz;
      moved += 1;

      for (let g = 0; g < this._maxLength; g += 1) {
        const idx = s * this._maxLength + g;
        this._anchors[idx * 2] = nx;
        this._anchors[idx * 2 + 1] = nz;
      }
    }

    if (moved > 0) {
      anchorAttr.needsUpdate = true;
    }
  }
}
